
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "@/hooks/use-toast";
import { RFPEstimate } from "@/types/project-types";
import { pointValues, getManDaysFromPoints } from "@/data/estimationData";

export const calculateTotals = (componentEstimates: RFPEstimate[]) => {
  return componentEstimates.reduce((acc, curr) => {
    return {
      simple_count: acc.simple_count + curr.simple_count,
      medium_count: acc.medium_count + curr.medium_count,
      complex_count: acc.complex_count + curr.complex_count,
      supercomplex_count: acc.supercomplex_count + curr.supercomplex_count,
      total_points: acc.total_points + curr.total_points,
      total_dev_days: parseFloat((acc.total_dev_days + curr.total_dev_days).toFixed(2)),
      total_days: parseFloat((acc.total_days + curr.total_days).toFixed(2)),
    };
  }, {
    simple_count: 0,
    medium_count: 0,
    complex_count: 0,
    supercomplex_count: 0,
    total_points: 0,
    total_dev_days: 0,
    total_days: 0,
  });
};

export const updateEstimate = (
  componentEstimates: RFPEstimate[],
  index: number,
  field: string,
  value: number
): RFPEstimate[] => {
  const newEstimates = [...componentEstimates];
  newEstimates[index] = {
    ...newEstimates[index],
    [field]: value
  };

  const componentType = newEstimates[index].component_type;
  
  const pointMap = pointValues[componentType as keyof typeof pointValues] || {
    'Simple': 1,
    'Medium': 3,
    'Complex': 5,
    'Supercomplex': 8
  };
  
  // Calculate total story points
  let totalPoints = 0;
  totalPoints += newEstimates[index].simple_count * pointMap.Simple;
  totalPoints += newEstimates[index].medium_count * pointMap.Medium;
  totalPoints += newEstimates[index].complex_count * pointMap.Complex;
  totalPoints += newEstimates[index].supercomplex_count * pointMap.Supercomplex;
  
  // Calculate days from story points
  let totalDays = 0;
  totalDays += newEstimates[index].simple_count * getManDaysFromPoints(pointMap.Simple);
  totalDays += newEstimates[index].medium_count * getManDaysFromPoints(pointMap.Medium);
  totalDays += newEstimates[index].complex_count * getManDaysFromPoints(pointMap.Complex);
  totalDays += newEstimates[index].supercomplex_count * getManDaysFromPoints(pointMap.Supercomplex);
  
  // Dev days is 40% of total days
  const devDays = totalDays * 0.4; 
  
  newEstimates[index].total_points = totalPoints;
  newEstimates[index].total_days = parseFloat(totalDays.toFixed(2));
  newEstimates[index].total_dev_days = parseFloat(devDays.toFixed(2));
  
  return newEstimates;
};

export const generatePDF = async (reportRef: React.RefObject<HTMLDivElement>, fileName: string) => {
  if (!reportRef.current) return false;
  
  try {
    toast({
      title: "Generating PDF",
      description: "Please wait...",
    });
    
    // Temporarily hide any problematic elements that might cause issues
    const problematicElements = reportRef.current.querySelectorAll('img');
    problematicElements.forEach(el => {
      el.style.display = 'none';
    });
    
    const canvas = await html2canvas(reportRef.current, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      onclone: (document) => {
        // Remove any elements that might cause issues in the clone
        const imgElements = document.querySelectorAll('img');
        imgElements.forEach(img => {
          img.remove();
        });
      }
    });
    
    // Restore hidden elements
    problematicElements.forEach(el => {
      el.style.display = '';
    });
    
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const imgWidth = 210;  // A4 width in mm
    const imgHeight = canvas.height * imgWidth / canvas.width;
    
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
    pdf.save(`${fileName.replace(/\s+/g, '_')}_estimation.pdf`);
    
    toast({
      title: "Success",
      description: "PDF downloaded successfully",
    });

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast({
      title: "Error",
      description: "Failed to generate PDF. Trying alternative method...",
      variant: "destructive",
    });
    
    // Alternative method if standard approach fails
    try {
      if (!reportRef.current) return false;
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add simple text content instead of rendering the whole component
      doc.setFontSize(20);
      doc.text(`${fileName} - Estimation Report`, 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add estimation data as text
      doc.setFontSize(16);
      doc.text("Estimation Summary", 20, 50);
      
      doc.save(`${fileName.replace(/\s+/g, '_')}_estimation.pdf`);
      
      toast({
        title: "Success",
        description: "Basic PDF downloaded successfully",
      });
      
      return true;
    } catch (error2) {
      console.error('Fallback error generating PDF:', error2);
      toast({
        title: "Error",
        description: "Failed to generate PDF with both methods. Please try again later.",
        variant: "destructive",
      });
      return false;
    }
  }
};

export const exportToExcel = (
  estimates: RFPEstimate[], 
  fileName: string,
  metadata: {
    projectName: string;
    description?: string;
    version?: number;
    updatedAt?: string;
    rfpId?: string;
  }
) => {
  // Get only components with values
  const filteredEstimates = estimates.filter(est => est.total_points > 0);

  if (filteredEstimates.length === 0) {
    toast({
      title: "No data to export",
      description: "Please add at least one component with values before exporting.",
      variant: "destructive",
    });
    return;
  }

  try {
    toast({
      title: "Generating Excel",
      description: "Please wait...",
    });

    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add metadata
    csvContent += `"Project:","${metadata.projectName}"\r\n`;
    if (metadata.description) {
      csvContent += `"Description:","${metadata.description}"\r\n`;
    }
    if (metadata.version) {
      csvContent += `"Version:","${metadata.version}"\r\n`;
    }
    csvContent += `"Date:","${new Date().toLocaleDateString()}"\r\n`;
    if (metadata.updatedAt) {
      csvContent += `"Last Updated:","${new Date(metadata.updatedAt).toLocaleDateString()}"\r\n`;
    }
    csvContent += "\r\n";

    // Headers
    const headers = [
      "Component Type", 
      "Simple", 
      "Medium", 
      "Complex", 
      "Supercomplex", 
      "Total Components", 
      "Dev Days", 
      "Total Days"
    ];
    csvContent += headers.join(",") + "\r\n";

    // Data rows
    filteredEstimates.forEach(est => {
      const row = [
        `"${est.component_type}"`,
        est.simple_count,
        est.medium_count,
        est.complex_count,
        est.supercomplex_count,
        est.total_points,
        est.total_dev_days.toFixed(1),
        est.total_days.toFixed(1)
      ];
      csvContent += row.join(",") + "\r\n";
    });

    // Calculate totals
    const totals = calculateTotals(filteredEstimates);
    const totalsRow = [
      '"TOTALS"',
      totals.simple_count,
      totals.medium_count,
      totals.complex_count,
      totals.supercomplex_count,
      totals.total_points,
      totals.total_dev_days.toFixed(1),
      totals.total_days.toFixed(1)
    ];
    csvContent += totalsRow.join(",") + "\r\n";

    // Add notes
    csvContent += "\r\n";
    csvContent += '"Notes:"\r\n';
    csvContent += '"- Story points follow the Fibonacci sequence (1, 2, 3, 5, 8, 13, 21)"\r\n';
    csvContent += '"- Points directly map to days - 1 point equals 1 day of total effort"\r\n';
    csvContent += '"- Development effort is approximately 40% of the total estimate"\r\n';
    csvContent += '"- Estimates include discovery, development, testing, and bug fixing"\r\n';

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${fileName.replace(/\s+/g, '_')}_estimation.csv`);
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    
    toast({
      title: "Success",
      description: "Excel export completed successfully",
    });
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    toast({
      title: "Error",
      description: "Failed to export to Excel. Please try again later.",
      variant: "destructive",
    });
  }
};
